
import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSearchParams } from 'react-router-dom'

import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import { Icons } from 'helpers/icons'
import { MainSettingsPage } from 'pages/settings/layout/MainSettingsPage'
import {
    disconnectSocialMedia,
    getGoogleConnectLink,
    getTwitterConnectLink,
    googleConnectCode,
    twitterConnect
} from 'services/endpoints/auth'
import { useSetUserProfile, useUserProfile } from 'store'

import { SetPassword } from './SetPassword'
import styles from './SocialMediaSettings.module.scss'

export const SocialMediaSettings = () => {
    const userData = useUserProfile()!
    const [searchParams, setSearchParams] = useSearchParams()
    const { t } = useTranslation()
    const modalData = useModalContext()
    const queryClient = useQueryClient()

    const handledGoogleCallback = useRef(false)

    const [socialMediaState, setSocialMediaState] = useState({
        twitter_connected: Boolean(userData.twitter),
        google_connected: Boolean(userData.google)
    })

    useEffect(() => {
        setSocialMediaState({
            twitter_connected: Boolean(userData.twitter),
            google_connected: Boolean(userData.google)
        })
    }, [userData])

    // Twitter callback.
    useEffect(() => {
        const oauth_token = searchParams.get('oauth_token')
        const oauth_verifier = searchParams.get('oauth_verifier')

        if (!oauth_token || !oauth_verifier) return

        setSearchParams(prev => {
            const next = new URLSearchParams(prev)

            next.delete('oauth_token')
            next.delete('oauth_verifier')

            return next
        }, { replace: true })

        const completeTwitterConnection = async () => {
            try {
                await twitterConnect({
                    oauth_token,
                    oauth_verifier
                })

                useSetUserProfile({
                    twitter: 1
                })

                setSocialMediaState(prev => ({
                    ...prev,
                    twitter_connected: true
                }))

                void queryClient.invalidateQueries('user-profile')
            } catch {
                addToast('error', t('error:errorLoginTwitter'))
            }
        }

        void completeTwitterConnection()
    }, [searchParams, setSearchParams, queryClient, t])

    // Google callback.
    useEffect(() => {
        const code = searchParams.get('code')
        const state = searchParams.get('state')
        const error = searchParams.get('error')

        if (!code && !error) return
        if (handledGoogleCallback.current) return

        handledGoogleCallback.current = true

        // Remove Google's callback parameters immediately.
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)

            next.delete('code')
            next.delete('state')
            next.delete('scope')
            next.delete('authuser')
            next.delete('prompt')
            next.delete('error')
            next.delete('error_description')

            return next
        }, { replace: true })

        if (error) {
            if (error !== 'access_denied') {
                addToast(
                    'error',
                    t('error:errorSomethingWentWrong')
                )
            }

            return
        }

        if (!code || !state) {
            addToast(
                'error',
                t('error:errorSomethingWentWrong')
            )

            return
        }

        const completeGoogleConnection = async () => {
            try {
                await googleConnectCode({
                    code,
                    state
                })

                useSetUserProfile({
                    google: 1
                })

                setSocialMediaState(prev => ({
                    ...prev,
                    google_connected: true
                }))

                void queryClient.invalidateQueries('user-profile')
            } catch (error) {
                console.error(
                    'Google connection failed:',
                    error
                )

                addToast(
                    'error',
                    t('error:errorSomethingWentWrong')
                )
            }
        }

        void completeGoogleConnection()
    }, [searchParams, setSearchParams, queryClient, t])

    const hasPassword =
        userData.has_password === true ||
        userData.has_password === 1

    // Connect Twitter using full-page navigation.
    const twtrSignIn = async () => {
        try {
            const response = await getTwitterConnectLink()
            const url = response?.data?.message?.target_url

            if (!url) {
                throw new Error(
                    'Twitter authorization URL missing'
                )
            }

            window.location.assign(url)
        } catch {
            addToast(
                'error',
                t('error:errorSomethingWentWrong')
            )
        }
    }

    // Connect Google using full-page navigation.
    const gglSignIn = async () => {
        try {
            const response = await getGoogleConnectLink()
            const url = response?.data?.message?.target_url

            if (!url) {
                throw new Error(
                    'Google authorization URL missing'
                )
            }

            window.location.assign(url)
        } catch (error) {
            console.error(
                'Unable to start Google connection:',
                error
            )

            addToast(
                'error',
                t('error:errorSomethingWentWrong')
            )
        }
    }

    const disconnectSocialAccount = async (
        mediaName: 'google' | 'twitter'
    ) => {
        try {
            await disconnectSocialMedia(mediaName)

            if (mediaName === 'twitter') {
                useSetUserProfile({
                    twitter: 0,
                    twitter_nickname: null
                })
            } else {
                useSetUserProfile({
                    google: 0
                })
            }

            setSocialMediaState(prev => ({
                ...prev,
                [`${mediaName}_connected`]: false
            }))
        } catch (error) {
            addToast(
                'error',
                t('error:errorSomethingWentWrong')
            )

            throw error
        }
    }

    const openSetPassword = (
        mediaName: 'google' | 'twitter'
    ) => {
        modalData.addModal?.(
            '',
            <SetPassword
                disconnectSocialAccount={disconnectSocialAccount}
                mediaName={mediaName}
                disconnectAccout={true}
            />,
            true,
            false
        )
    }

    const handleDisconnect = (
        mediaName: 'google' | 'twitter'
    ) => {
        if (!hasPassword) {
            openSetPassword(mediaName)
            return
        }

        void disconnectSocialAccount(mediaName)
    }

    const socialMediaCards = [
        {
            icon: Icons.twitter_small,
            body: t('twitter'),
            currentData: userData.twitter_nickname
                ? `@${userData.twitter_nickname}`
                : undefined,
            hasToggleWithText: true,
            toggleText: socialMediaState.twitter_connected
                ? t('connected')
                : t('connect'),
            toggleActive: socialMediaState.twitter_connected,
            toggleFn: () => {
                if (socialMediaState.twitter_connected) {
                    handleDisconnect('twitter')
                    return
                }

                if (userData.email) {
                    void twtrSignIn()
                }
            },
            customClass: styles.socialCard
        },
        {
            icon: Icons.google,
            body: t('google'),
            hasToggleWithText: true,
            toggleText: socialMediaState.google_connected
                ? t('connected')
                : t('connect'),
            toggleActive: socialMediaState.google_connected,
            toggleFn: () => {
                if (socialMediaState.google_connected) {
                    handleDisconnect('google')
                    return
                }

                if (userData.email) {
                    void gglSignIn()
                }
            },
            customClass: styles.socialCard
        }
    ]

    return <MainSettingsPage settingsTop={socialMediaCards} />
}
